import { Link, NavLink } from "react-router-dom";
import { Menu } from "lucide-react";
import { useState } from "react";
import { Button } from "@/components/ui/button";
import { useCity } from "@/contexts/CityContext";
import { useAuth } from "@/contexts/AuthContext";
import { cn } from "@/lib/utils";

const NAV_ITEMS = [
  { label: "Alerts", to: "/alerts" },
  { label: "Directory", to: "/directory" },
  { label: "Resources", to: "/resources" },
] as const;

/**
 * Top navigation. Editorial spirit: the wordmark sits alone on the left
 * (like a masthead), nav links are uppercase small caps, the CTA is the
 * only coloured element.
 *
 * The city name appears as a secondary label next to the wordmark. A future
 * <CitySwitcher /> can replace this pill without restructuring the nav.
 */
export function Nav() {
  const { city, link } = useCity();
  const { isAuthenticated, user, roles, logout } = useAuth();
  const [mobileOpen, setMobileOpen] = useState(false);

  const dashboardPath = roles.includes("specialist")
    ? link("/specialist-dashboard")
    : roles.includes("gp")
      ? link("/gp-dashboard")
      : link("/");

  return (
    <header className="sticky top-0 z-30 border-b border-pb-border/60 bg-pb-bg/80 backdrop-blur-md">
      <div className="mx-auto flex h-16 max-w-6xl items-center justify-between gap-6 px-4 md:px-8">
        {/* Wordmark + city */}
        <Link
          to={link("/")}
          className="flex items-center gap-3 group"
          aria-label={`PulseBoard ${city.name} home`}
        >
          <span className="relative inline-flex h-2.5 w-2.5">
            <span className="absolute inset-0 animate-pb-ring-expand rounded-full bg-pb-primary/40" />
            <span className="relative h-2.5 w-2.5 animate-pb-breathe rounded-full bg-pb-primary" />
          </span>
          <span className="font-display text-xl font-semibold tracking-tight text-pb-ink">
            PulseBoard
          </span>
          <span
            aria-hidden
            className="hidden text-[10px] font-medium uppercase tracking-[0.2em] text-pb-ink-3 sm:inline"
          >
            · {city.name}
          </span>
        </Link>

        {/* Desktop links */}
        <nav className="hidden items-center gap-8 md:flex" aria-label="Primary">
          {NAV_ITEMS.map((item) => (
            <NavLink
              key={item.to}
              to={link(item.to)}
              className={({ isActive }) =>
                cn(
                  "text-xs font-medium uppercase tracking-[0.2em] transition-colors",
                  isActive ? "text-pb-primary" : "text-pb-ink-2 hover:text-pb-ink",
                )
              }
            >
              {item.label}
            </NavLink>
          ))}
        </nav>

        {/* Right cluster */}
        <div className="hidden items-center gap-3 md:flex">
          {isAuthenticated ? (
            <>
              <Button variant="ghost" size="sm" asChild>
                <Link to={dashboardPath}>Dashboard</Link>
              </Button>
              <Button variant="outline" size="sm" onClick={() => void logout()}>
                Sign out
                <span className="sr-only"> ({user?.name})</span>
              </Button>
            </>
          ) : (
            <Button size="sm" asChild>
              <Link to={link("/auth")}>Sign in</Link>
            </Button>
          )}
        </div>

        {/* Mobile menu toggle */}
        <button
          type="button"
          className="inline-flex h-10 w-10 items-center justify-center rounded-md text-pb-ink md:hidden"
          onClick={() => setMobileOpen((o) => !o)}
          aria-expanded={mobileOpen}
          aria-controls="mobile-nav"
          aria-label="Toggle navigation"
        >
          <Menu className="h-5 w-5" />
        </button>
      </div>

      {/* Mobile drawer */}
      {mobileOpen && (
        <nav
          id="mobile-nav"
          className="border-t border-pb-border/60 bg-pb-surface md:hidden"
          aria-label="Primary mobile"
        >
          <div className="flex flex-col px-4 py-3">
            {NAV_ITEMS.map((item) => (
              <NavLink
                key={item.to}
                to={link(item.to)}
                onClick={() => setMobileOpen(false)}
                className={({ isActive }) =>
                  cn(
                    "py-2 text-sm font-medium",
                    isActive ? "text-pb-primary" : "text-pb-ink-2",
                  )
                }
              >
                {item.label}
              </NavLink>
            ))}
            <div className="mt-2 border-t border-pb-border pt-3">
              {isAuthenticated ? (
                <>
                  <Button asChild variant="outline" size="sm" className="w-full">
                    <Link to={dashboardPath} onClick={() => setMobileOpen(false)}>
                      Dashboard
                    </Link>
                  </Button>
                  <button
                    onClick={() => void logout()}
                    className="mt-2 w-full py-2 text-left text-sm text-pb-ink-2"
                  >
                    Sign out
                  </button>
                </>
              ) : (
                <Button asChild size="sm" className="w-full">
                  <Link to={link("/auth")} onClick={() => setMobileOpen(false)}>
                    Sign in
                  </Link>
                </Button>
              )}
            </div>
          </div>
        </nav>
      )}
    </header>
  );
}
